import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["id", "alternativeText"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import React, { useState, useEffect, forwardRef } from 'react';
import { useDebounce } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import groupBy from 'utils/groupBy';
import { scrollIntoViewIfNeeded } from 'utils/ui';
import { isBadgeImageSelected } from 'types/badges';
import useUniqueId from 'hooks/useUniqueId';
import { useFetchBadgeImages } from 'actions/badges';
import useItemPickerView, { VIEW_KEYS } from 'components/ItemPicker/useItemPickerView';
import Box from 'components/Box';
import Button from 'components/Button';
import EmptyStateController from 'components/EmptyStateController';
import InlineList from 'components/InlineList';
import Modal from 'components/Modal';
import { Grid, GridItem } from 'components/Grid';
import Stack from 'components/Stack';
import Text from 'components/Text';
import LoadMoreOnScroll from 'components/LoadMoreOnScroll';
import ItemPickerUI from 'components/ItemPicker/ItemPickerUI';
import BadgeImage from 'components/BadgeImage';
import SelectableBadgeImage from 'components/SelectableBadgeImage';
import { BadgeImageDetailsModal } from 'screens/ContentEditorModals';
var SelectorBadgeImageItem = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var badgeImage = _ref.badgeImage,
    isSelected = _ref.isSelected,
    isEditable = _ref.isEditable,
    onSelect = _ref.onSelect,
    onDelete = _ref.onDelete,
    parentPlaylistId = _ref.parentPlaylistId;
  return /*#__PURE__*/React.createElement(GridItem, {
    size: "1/2",
    mSize: "1/4"
  }, /*#__PURE__*/React.createElement(SelectableBadgeImage, {
    ref: ref,
    isSelected: isSelected,
    isEditable: isEditable,
    badgeImage: badgeImage,
    parentPlaylistId: parentPlaylistId,
    label: badgeImage.imageAlternativeText,
    onSelect: onSelect,
    onDelete: onDelete
  }, /*#__PURE__*/React.createElement(BadgeImage, {
    size: 80,
    src: badgeImage.pictureUrl,
    alt: "",
    variant: badgeImage.style
  })));
});
SelectorBadgeImageItem.displayName = 'SelectorBadgeImageItem';
function SelectorNoImageItem(_ref2) {
  var isSelected = _ref2.isSelected,
    onSelect = _ref2.onSelect,
    parentPlaylistId = _ref2.parentPlaylistId;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  return /*#__PURE__*/React.createElement(GridItem, {
    size: "1/2",
    mSize: "1/4"
  }, /*#__PURE__*/React.createElement(SelectableBadgeImage, {
    isSelected: isSelected,
    label: t('content_type_no_image'),
    onSelect: onSelect,
    parentPlaylistId: parentPlaylistId,
    badgeImage: null
  }));
}
var DEBOUNCE_INTERVAL = 500; // no more than 1 search query every 0.5 seconds

function BadgeImagePicker(_ref3) {
  var initialSelectedBadgeImage = _ref3.initialSelectedBadgeImage,
    parentPlaylistId = _ref3.parentPlaylistId,
    onChange = _ref3.onChange,
    onClose = _ref3.onClose,
    onBadgeImageDeletion = _ref3.onBadgeImageDeletion;
  var _useTranslation2 = useTranslation(),
    t = _useTranslation2.t;
  var uniqueId = useUniqueId();
  var customBadgeTitleId = "".concat(uniqueId, "-custom-badge-title");
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isBadgeImageModalOpen = _useState2[0],
    setBadgeImageModalOpen = _useState2[1];
  var _useState3 = useState(initialSelectedBadgeImage || null),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedBadgeImage = _useState4[0],
    setSelectedBadgeImage = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedBadgeElement = _useState6[0],
    setSelectedBadgeElement = _useState6[1];
  var isNewSelection = (selectedBadgeImage === null || selectedBadgeImage === void 0 ? void 0 : selectedBadgeImage.imageId) !== (initialSelectedBadgeImage === null || initialSelectedBadgeImage === void 0 ? void 0 : initialSelectedBadgeImage.imageId);
  var _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    searchValue = _useState8[0],
    setSearchValue = _useState8[1];
  var _useDebounce = useDebounce(searchValue, DEBOUNCE_INTERVAL),
    _useDebounce2 = _slicedToArray(_useDebounce, 1),
    debouncedSearchValue = _useDebounce2[0];
  // Only use a debounced value if a search term exists
  // This prevents a laggy UI when clearing the search
  var searchQuery = searchValue ? debouncedSearchValue : '';
  var _useItemPickerView = useItemPickerView({
      title: t('actions_pick_badge_image'),
      searchValue: searchValue,
      clearSearchValue: function clearSearchValue() {
        return setSearchValue('');
      }
    }),
    view = _useItemPickerView.view,
    isSearchView = _useItemPickerView.isSearchView,
    isSelectionView = _useItemPickerView.isSelectionView,
    changeView = _useItemPickerView.changeView;

  // Fetch all the images: system and custom...
  var _useFetchBadgeImages = useFetchBadgeImages({
      search: searchQuery
    }),
    unformattedBadgeImages = _useFetchBadgeImages.items,
    fetchNextPage = _useFetchBadgeImages.fetchNextPage,
    areBadgeImagesLoading = _useFetchBadgeImages.isLoading,
    didBadgeImagesError = _useFetchBadgeImages.isError,
    hasNextPage = _useFetchBadgeImages.hasNextPage,
    isFetchingNextPage = _useFetchBadgeImages.isFetchingNextPage;
  var badgeImages = unformattedBadgeImages.map(function (_ref4) {
    var id = _ref4.id,
      alternativeText = _ref4.alternativeText,
      badgeImage = _objectWithoutProperties(_ref4, _excluded);
    return _objectSpread({
      imageId: id,
      imageAlternativeText: alternativeText
    }, badgeImage);
  });

  // Split the response into two groups by origin...
  var _groupBy = groupBy(badgeImages, function (_ref5) {
      var origin = _ref5.origin;
      return origin;
    }),
    _groupBy$system = _groupBy.system,
    systemBadgeImages = _groupBy$system === void 0 ? [] : _groupBy$system,
    _groupBy$custom = _groupBy.custom,
    customBadgeImages = _groupBy$custom === void 0 ? [] : _groupBy$custom;
  var hasSystemBadgeImages = !areBadgeImagesLoading && systemBadgeImages.length > 0;
  var hasCustomBadgeImages = !areBadgeImagesLoading && customBadgeImages.length > 0;

  // Scroll the selected badge into view when the picker is opened
  useEffect(function () {
    if ((!areBadgeImagesLoading || isNewSelection) && selectedBadgeElement) {
      scrollIntoViewIfNeeded(selectedBadgeElement, isNewSelection ? 'smooth' : 'instant');
    }
  }, [areBadgeImagesLoading, isNewSelection, selectedBadgeElement]);
  function selectBadgeImage(badgeImage) {
    setSelectedBadgeImage(badgeImage);
  }
  function selectNone() {
    setSelectedBadgeImage(null);
  }
  function submit() {
    onChange(selectedBadgeImage);
    onClose();
  }
  function resetAfterDelete() {
    if (typeof onBadgeImageDeletion === 'function') {
      onBadgeImageDeletion(systemBadgeImages.at(0));
    }
    setSelectedBadgeImage(systemBadgeImages.at(0) || null);
  }

  // Note: `isBadgeImageSelected` is a type guard function that checks for the badge image id
  var isImageSelected = selectedBadgeImage && isBadgeImageSelected(selectedBadgeImage);
  return /*#__PURE__*/React.createElement(Modal, {
    width: "650",
    "aria-label": t('actions_pick_badge_image'),
    onRequestClose: onClose
  }, /*#__PURE__*/React.createElement(ItemPickerUI, {
    isSingleItemPicker: true,
    isSubmitDisabled: !isNewSelection,
    view: view,
    onChangeView: changeView,
    selectedItemCount: 1,
    searchValue: searchValue,
    onSearchValueChange: setSearchValue,
    onSubmit: submit,
    onClose: onClose,
    contentHeight: 480,
    labels: {
      searchPlaceholder: t('actions_search_by_alt_text_hellip')
    }
  }, /*#__PURE__*/React.createElement(Box, {
    p: "m"
  }, isSelectionView ? /*#__PURE__*/React.createElement(Grid, {
    hasBottomSpacing: true
  }, isImageSelected ? /*#__PURE__*/React.createElement(SelectorBadgeImageItem, {
    ref: setSelectedBadgeElement,
    isSelected: true,
    isEditable: false,
    badgeImage: selectedBadgeImage,
    onSelect: function onSelect() {
      return selectBadgeImage(selectedBadgeImage);
    },
    onDelete: resetAfterDelete,
    parentPlaylistId: parentPlaylistId
  }) : /*#__PURE__*/React.createElement(SelectorNoImageItem, {
    isSelected: true,
    onSelect: selectNone,
    parentPlaylistId: parentPlaylistId
  })) : /*#__PURE__*/React.createElement(Stack, {
    spacing: "m"
  }, /*#__PURE__*/React.createElement("fieldset", null, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    as: "legend",
    mb: "xs"
  }, t('content_type_system_image', {
    count: systemBadgeImages.length
  })), /*#__PURE__*/React.createElement(EmptyStateController, {
    isLoading: isSearchView && areBadgeImagesLoading,
    isError: didBadgeImagesError,
    isEmpty: isSearchView && !hasSystemBadgeImages,
    emptyContent: /*#__PURE__*/React.createElement("p", null, t('error_empty'))
  }, /*#__PURE__*/React.createElement(Grid, {
    hasBottomSpacing: true
  }, !isSearchView && /*#__PURE__*/React.createElement(SelectorNoImageItem, {
    isSelected: selectedBadgeImage === null,
    onSelect: selectNone,
    parentPlaylistId: parentPlaylistId
  }), systemBadgeImages.map(function (badgeImage) {
    var isSelected = (badgeImage === null || badgeImage === void 0 ? void 0 : badgeImage.imageId) === (selectedBadgeImage === null || selectedBadgeImage === void 0 ? void 0 : selectedBadgeImage.imageId);
    return /*#__PURE__*/React.createElement(SelectorBadgeImageItem, {
      key: badgeImage.imageId,
      badgeImage: badgeImage,
      ref: isSelected ? setSelectedBadgeElement : null,
      isEditable: false,
      isSelected: isSelected,
      onSelect: function onSelect() {
        return selectBadgeImage(badgeImage);
      },
      onDelete: resetAfterDelete,
      parentPlaylistId: parentPlaylistId
    });
  })))), /*#__PURE__*/React.createElement("fieldset", {
    "aria-labelledby": customBadgeTitleId
  }, /*#__PURE__*/React.createElement(InlineList, {
    spacing: "xxs",
    as: "span",
    itemAs: "span"
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    as: "legend",
    mb: "xs"
    // We need to assign the label to the fieldset explicitly
    // via ID, as the legend isn't the fieldset's first child
    ,
    id: customBadgeTitleId
  }, t('content_type_custom_image', {
    count: customBadgeImages.length
  })), view.key === VIEW_KEYS.DEFAULT && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "transparent",
    icon: "plus",
    onClick: function onClick() {
      return setBadgeImageModalOpen(true);
    }
  }, t('actions_upload')), isBadgeImageModalOpen && /*#__PURE__*/React.createElement(BadgeImageDetailsModal, {
    onAfterUpload: setSelectedBadgeImage,
    onClose: function onClose() {
      return setBadgeImageModalOpen(false);
    }
  })), /*#__PURE__*/React.createElement(EmptyStateController, {
    isLoading: areBadgeImagesLoading,
    isError: didBadgeImagesError,
    isEmpty: !hasCustomBadgeImages,
    emptyContent: isSearchView ? /*#__PURE__*/React.createElement("p", null, t('error_empty')) : /*#__PURE__*/React.createElement(Text, {
      as: "p"
    }, t('error_no_custom_badges'))
  }, /*#__PURE__*/React.createElement(Grid, {
    hasBottomSpacing: true
  }, customBadgeImages.map(function (badgeImage) {
    var isSelected = badgeImage.imageId === (selectedBadgeImage === null || selectedBadgeImage === void 0 ? void 0 : selectedBadgeImage.imageId);
    return /*#__PURE__*/React.createElement(SelectorBadgeImageItem, {
      key: badgeImage.imageId,
      badgeImage: badgeImage,
      ref: isSelected ? setSelectedBadgeElement : null,
      isEditable: true,
      isSelected: isSelected,
      onSelect: function onSelect() {
        return selectBadgeImage(badgeImage);
      },
      onDelete: resetAfterDelete,
      parentPlaylistId: parentPlaylistId
    });
  })), /*#__PURE__*/React.createElement(LoadMoreOnScroll, {
    hasNextPage: hasNextPage,
    isFetchingNextPage: isFetchingNextPage,
    onFetchNextPage: fetchNextPage
  })))))));
}
export default BadgeImagePicker;